import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Camera, Trash2, GripVertical, Layers, ImagePlus, Minus } from 'lucide-react'
import { Scene } from '../../types'
import { useProjectStore } from '../../stores/projectStore'
import BulkAddScenesDialog from './BulkAddScenesDialog'
import SceneSettingsDialog from './SceneSettingsDialog'
import CreateSceneDialog from './CreateSceneDialog'

interface ScenesListProps {
  scenes: Scene[]
  selectedSceneId: string | null
  onSceneSelect: (sceneId: string) => void
  onCreateScene: () => void
  projectId: string
  projectAspectRatio: string
  thumbnailScale?: number
  onThumbnailScaleChange?: (scale: number) => void
}

const ScenesList: React.FC<ScenesListProps> = ({
  scenes,
  selectedSceneId,
  onSceneSelect,
  onCreateScene,
  projectId,
  projectAspectRatio,
  thumbnailScale = 1,
  onThumbnailScaleChange
}) => {
  const [hoveredSceneId, setHoveredSceneId] = useState<string | null>(null)
  const [showBulkAddDialog, setShowBulkAddDialog] = useState(false)
  const [deleteConfirmSceneId, setDeleteConfirmSceneId] = useState<string | null>(null)
  const [editingSceneId, setEditingSceneId] = useState<string | null>(null)
  const [editingTitle, setEditingTitle] = useState('')
  const [settingsScene, setSettingsScene] = useState<Scene | null>(null)
  const [isDroppingImage, setIsDroppingImage] = useState(false)
  const [droppedImageId, setDroppedImageId] = useState<string | null>(null)
  const [showCreateSceneFromImage, setShowCreateSceneFromImage] = useState(false)
  const [imageDropTargetSceneId, setImageDropTargetSceneId] = useState<string | null>(null)
  const [draggingSceneId, setDraggingSceneId] = useState<string | null>(null)
  const editInputRef = useRef<HTMLInputElement>(null)
  const { deleteScene, reorderScenes, updateScene, moveImageToScene } = useProjectStore()

  // Focus input when editing starts
  useEffect(() => {
    if (editingSceneId && editInputRef.current) {
      editInputRef.current.focus()
      editInputRef.current.select()
    }
  }, [editingSceneId])

  const handleStartEditing = (scene: Scene, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingSceneId(scene.id)
    setEditingTitle(scene.title)
  }

  const handleSaveTitle = async () => {
    if (!editingSceneId || !editingTitle.trim()) {
      setEditingSceneId(null)
      return
    }
    
    try {
      await updateScene(editingSceneId, { title: editingTitle.trim() })
    } catch (error) {
      console.error('Failed to update scene title:', error)
    }
    setEditingSceneId(null)
  }

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveTitle()
    } else if (e.key === 'Escape') {
      setEditingSceneId(null)
    }
  }

  const handleThumbnailDoubleClick = (scene: Scene, e: React.MouseEvent) => {
    e.stopPropagation()
    setSettingsScene(scene)
  }

  const handleDeleteScene = async (sceneId: string) => {
    setDeleteConfirmSceneId(sceneId)
  }

  const confirmDeleteScene = async () => {
    if (!deleteConfirmSceneId) return
    try {
      await deleteScene(deleteConfirmSceneId)
      setDeleteConfirmSceneId(null)
    } catch (error) {
      console.error('Failed to delete scene:', error)
    }
  }

  const handleDragStart = (e: React.DragEvent, sceneId: string) => {
    e.dataTransfer.setData('sceneId', sceneId)
    e.dataTransfer.effectAllowed = 'move'
    setDraggingSceneId(sceneId)
  }

  const handleDragEnd = () => {
    setDraggingSceneId(null)
    setImageDropTargetSceneId(null)
  }

  const handleDragOver = (e: React.DragEvent, sceneId?: string) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Check if dragging an image from gallery
    const imageId = e.dataTransfer.types.includes('imageid')
    if (imageId && sceneId) {
      setImageDropTargetSceneId(sceneId)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setImageDropTargetSceneId(null)
  }

  const handleDrop = async (e: React.DragEvent, targetSceneId: string) => {
    e.preventDefault()
    e.stopPropagation()
    setImageDropTargetSceneId(null)
    setDraggingSceneId(null)
    
    // Check if dropping an image from gallery
    const imageId = e.dataTransfer.getData('imageId')
    const action = e.dataTransfer.getData('action')
    
    if (imageId && action === 'createScene') {
      // Move image to this scene instead of creating new
      try {
        await moveImageToScene(imageId, targetSceneId)
      } catch (error) {
        console.error('Failed to move image to scene:', error)
      }
      return
    }
    
    // Handle scene reordering
    const draggedSceneId = e.dataTransfer.getData('sceneId')
    
    if (!draggedSceneId || draggedSceneId === targetSceneId) return

    const draggedIndex = scenes.findIndex(s => s.id === draggedSceneId)
    const targetIndex = scenes.findIndex(s => s.id === targetSceneId)
    
    if (draggedIndex === -1 || targetIndex === -1) return

    const newScenes = [...scenes]
    const [draggedScene] = newScenes.splice(draggedIndex, 1)
    newScenes.splice(targetIndex, 0, draggedScene)

    try {
      await reorderScenes(projectId, newScenes.map(s => s.id))
    } catch (error) {
      console.error('Failed to reorder scenes:', error)
    }
  }

  // Calculate proportional scale (0.4 to 1.0 = 40% to 100% width)
  // At max, the scene card should fill the full panel width
  const scalePercent = Math.round(thumbnailScale * 100)
  
  // Calculate text and icon sizes based on scale
  const titleSize = thumbnailScale < 0.6 ? 'text-xs' : thumbnailScale < 0.8 ? 'text-sm' : 'text-base'
  const numberSize = thumbnailScale < 0.6 ? 'text-[10px]' : 'text-xs'
  const iconSize = thumbnailScale < 0.6 ? 'w-5 h-5' : thumbnailScale < 0.8 ? 'w-6 h-6' : 'w-8 h-8'
  const padding = thumbnailScale < 0.6 ? 'p-1' : thumbnailScale < 0.8 ? 'p-1.5' : 'p-2'

  return (
    <div className="h-full flex flex-col">
      {/* Thumbnail Size Control */}
      {onThumbnailScaleChange && (
        <div className="px-3 py-2 border-b border-glass-border flex items-center justify-between">
          <span className="text-white/50 text-xs">Size</span>
          <div className="flex items-center space-x-1">
            <Minus className="w-3 h-3 text-white/40" />
            <input
              type="range"
              min="0.4"
              max="1.0"
              step="0.05"
              value={thumbnailScale}
              onChange={(e) => onThumbnailScaleChange(parseFloat(e.target.value))}
              className="w-20 accent-purple-500 h-1"
            />
            <Plus className="w-3 h-3 text-white/40" />
          </div>
        </div>
      )}
      
      {/* Scenes List */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {scenes.length === 0 ? (
          <div className="text-center py-8">
            <Camera className="w-12 h-12 text-primary-400 mx-auto mb-4" />
            <p className="font-body font-light text-white/70 mb-4">No scenes yet</p>
            <button
              onClick={onCreateScene}
              className="glass-button bg-primary-500/30 hover:bg-primary-500/40 text-white px-4 py-2 font-ui font-normal"
            >
              Create First Scene
            </button>
          </div>
        ) : (
          <>
            {scenes.map((scene, index) => (
              <div key={scene.id} className="flex flex-col items-center">
                {/* Scene Item - proportionally scaled */}
                <div
                  className={`scene-item group relative transition-all duration-200 ${padding} ${
                    selectedSceneId === scene.id 
                      ? 'ring-2 ring-purple-400 shadow-lg shadow-purple-500/20 hover:bg-purple-500/30' 
                      : 'hover:bg-glass-medium'
                  } ${
                    draggingSceneId === scene.id ? 'opacity-50 scale-95' : ''
                  } ${
                    draggingSceneId && draggingSceneId !== scene.id ? 'border-2 border-dashed border-purple-500/50' : ''
                  } ${
                    imageDropTargetSceneId === scene.id ? 'ring-2 ring-green-400 bg-green-500/20' : ''
                  }`}
                  style={{ width: `${scalePercent}%` }}
                  onMouseEnter={() => setHoveredSceneId(scene.id)}
                  onMouseLeave={() => setHoveredSceneId(null)}
                  draggable={!editingSceneId}
                  onDragStart={(e) => handleDragStart(e, scene.id)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, scene.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, scene.id)}
                  onClick={() => onSceneSelect(scene.id)}
                >
                  {/* Drag Handle */}
                  <div className="absolute top-2 left-2 drag-handle cursor-grab active:cursor-grabbing">
                    <GripVertical className="w-4 h-4 text-white/50" />
                  </div>

                  {/* Image Drop Indicator */}
                  {imageDropTargetSceneId === scene.id && (
                    <div className="absolute inset-0 flex items-center justify-center bg-green-500/20 rounded-lg z-10 pointer-events-none">
                      <div className="text-green-300 text-sm font-medium flex items-center space-x-2">
                        <ImagePlus className="w-5 h-5" />
                        <span>Drop to move here</span>
                      </div>
                    </div>
                  )}

                  {/* Scene Thumbnail - Double-click to open settings */}
                  <div 
                    className="scene-thumbnail mb-1 relative overflow-hidden cursor-pointer w-full"
                    style={{ 
                      aspectRatio: projectAspectRatio === '16:9' ? '16/9' : 
                                  projectAspectRatio === '4:3' ? '4/3' : 
                                  projectAspectRatio === '1:1' ? '1/1' : '16/9'
                    }}
                    onDoubleClick={(e) => handleThumbnailDoubleClick(scene, e)}
                    title="Double-click to edit scene settings"
                  >
                    {scene.thumbnailUrl ? (
                      <img 
                        src={scene.thumbnailUrl} 
                        alt={scene.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-primary-800 flex items-center justify-center">
                        <Camera className={`${iconSize} text-primary-400`} />
                      </div>
                    )}
                  </div>

                  {/* Scene Info - scaled proportionally */}
                  <div className="space-y-0.5">
                    <div className="flex items-center space-x-1">
                      <span className={`font-ui font-normal text-white/50 ${numberSize}`}>
                        #{String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    
                    {/* Scene Title - Double-click to edit inline */}
                    {editingSceneId === scene.id ? (
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onBlur={handleSaveTitle}
                        onKeyDown={handleTitleKeyDown}
                        onClick={(e) => e.stopPropagation()}
                        className={`font-headline text-white font-medium bg-white/10 border border-primary-500/50 rounded px-1 py-0.5 w-full focus:outline-none focus:ring-2 focus:ring-primary-500 ${titleSize}`}
                        maxLength={100}
                      />
                    ) : (
                      <h3 
                        className={`font-headline text-white font-medium cursor-text hover:bg-white/5 rounded px-1 -mx-1 transition-colors truncate ${titleSize}`}
                        onDoubleClick={(e) => handleStartEditing(scene, e)}
                        title="Double-click to rename"
                      >
                        {scene.title}
                      </h3>
                    )}
                    
                    {/* Scene Details - Hover Only (simplified, no description) - hide on small scale */}
                    {thumbnailScale >= 0.6 && (
                      <div className="scene-details">
                        <p className={`font-body font-light text-white/50 ${numberSize}`}>
                          {scene.mainImageId ? 'Main image set' : 'No main image'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Actions Menu */}
                  <AnimatePresence>
                    {hoveredSceneId === scene.id && !editingSceneId && (
                      <motion.div
                        className="absolute top-2 right-2"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteScene(scene.id)
                          }}
                          className="glass-button p-1.5 hover:bg-red-500/20 text-red-400 hover:text-red-300"
                          title="Delete scene"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Add Scene Button Between Scenes - Full width */}
                {index < scenes.length - 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onCreateScene()
                    }}
                    className="w-full py-1.5 flex items-center justify-center gap-2 hover:bg-purple-500/20 opacity-30 hover:opacity-100 transition-all rounded-lg border border-transparent hover:border-purple-500/30"
                    title="Add scene here"
                  >
                    <div className="flex-1 h-px bg-white/20" />
                    <Plus className="w-3 h-3 text-white/70" />
                    <div className="flex-1 h-px bg-white/20" />
                  </button>
                )}
              </div>
            ))}

            {/* Drop Zone for Creating Scene from Gallery Image */}
            <div 
              className={`border-2 border-dashed rounded-lg p-4 transition-all mb-2 ${
                isDroppingImage 
                  ? 'border-green-400 bg-green-500/20' 
                  : 'border-white/20 hover:border-white/40'
              }`}
              onDragOver={(e) => {
                e.preventDefault()
                const imageId = e.dataTransfer.types.includes('imageid')
                if (imageId || e.dataTransfer.types.includes('text/plain')) {
                  setIsDroppingImage(true)
                }
              }}
              onDragLeave={(e) => {
                e.preventDefault()
                setIsDroppingImage(false)
              }}
              onDrop={(e) => {
                e.preventDefault()
                setIsDroppingImage(false)
                const imageId = e.dataTransfer.getData('imageId')
                const action = e.dataTransfer.getData('action')
                if (imageId && action === 'createScene') {
                  setDroppedImageId(imageId)
                  setShowCreateSceneFromImage(true)
                }
              }}
            >
              <div className="flex items-center justify-center space-x-2 text-white/50">
                <ImagePlus className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {isDroppingImage ? 'Drop to create scene' : 'Drop image here'}
                </span>
              </div>
            </div>

            {/* Add Scene Buttons at Bottom */}
            <div className="flex flex-col space-y-2 pt-3 border-t border-glass-border mt-2">
              <button
                onClick={onCreateScene}
                className="glass-button bg-purple-500/30 hover:bg-purple-500/40 text-white px-4 py-2.5 font-ui font-normal flex items-center justify-center space-x-2 w-full"
              >
                <Plus className="w-4 h-4" />
                <span>Add Scene</span>
              </button>
              
              <button
                onClick={() => setShowBulkAddDialog(true)}
                className="glass-button bg-primary-600/20 hover:bg-primary-600/30 text-white/70 px-3 py-1.5 font-ui font-normal flex items-center justify-center space-x-2 w-full text-sm"
              >
                <Layers className="w-3.5 h-3.5" />
                <span>Add Multiple</span>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Bulk Add Scenes Dialog */}
      <BulkAddScenesDialog
        open={showBulkAddDialog}
        onClose={() => setShowBulkAddDialog(false)}
        projectId={projectId}
      />

      {/* Scene Settings Dialog */}
      <SceneSettingsDialog
        open={!!settingsScene}
        onClose={() => setSettingsScene(null)}
        scene={settingsScene}
      />

      {/* Create Scene from Dropped Image Dialog */}
      <CreateSceneDialog
        open={showCreateSceneFromImage}
        onClose={() => {
          setShowCreateSceneFromImage(false)
          setDroppedImageId(null)
        }}
        projectId={projectId}
        initialImageId={droppedImageId}
      />

      {/* Delete Confirmation Dialog */}
      <AnimatePresence>
        {deleteConfirmSceneId && (
          <motion.div
            className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDeleteConfirmSceneId(null)}
          >
            <motion.div
              className="glass-card w-full max-w-md border border-red-500/30 shadow-2xl overflow-hidden"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with gradient */}
              <div className="bg-gradient-to-r from-red-500/20 to-red-600/20 border-b border-red-500/30 p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-red-500/30 rounded-full flex items-center justify-center ring-2 ring-red-500/50">
                    <Trash2 className="w-6 h-6 text-red-300" />
                  </div>
                  <div>
                    <h2 className="text-xl font-headline text-white font-semibold">Delete Scene</h2>
                    <p className="text-red-200/70 text-sm font-body">This action is permanent</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-white/80 font-body leading-relaxed mb-2">
                  Are you sure you want to delete scene <span className="font-semibold text-white">"{scenes.find(s => s.id === deleteConfirmSceneId)?.title}"</span>?
                </p>
                <p className="text-white/60 font-body text-sm">
                  All images in this scene will be permanently deleted. This action cannot be undone.
                </p>
              </div>

              {/* Actions */}
              <div className="flex space-x-3 p-6 pt-0">
                <button
                  onClick={() => setDeleteConfirmSceneId(null)}
                  className="flex-1 glass-button bg-white/5 hover:bg-white/10 text-white px-4 py-2.5 font-ui font-medium transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteScene}
                  className="flex-1 glass-button bg-red-500/40 hover:bg-red-500/50 text-white px-4 py-2.5 font-ui font-medium transition-all shadow-lg shadow-red-500/20"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ScenesList 